import { useState, useEffect, useCallback } from 'react';
import { classroomAPI, chainAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Users, Plus, Send, UserPlus, CheckCircle2, Clock, X, Loader2, ThumbsUp, ThumbsDown, AlertCircle } from 'lucide-react';
import ChainKeeperRating from './ChainKeeperRating';

const MIN_FAIL_JUSTIFICATION = 40;
const MIN_PASS_NUDGE = 20;

function TeacherVerdict({ submissionId, onSubmitted }) {
  const [verdict, setVerdict] = useState('');
  const [existing, setExisting] = useState(null);
  const [justification, setJustification] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    classroomAPI.getVerdict(submissionId).then((r) => {
      if (r.data?.verdict) {
        setExisting(r.data);
        setVerdict(r.data.verdict);
        setJustification(r.data.justification || '');
      }
    }).catch(() => {});
  }, [submissionId]);

  const justLen = justification.trim().length;
  const failBlocked = verdict === 'fail' && justLen < MIN_FAIL_JUSTIFICATION;
  const passNudge = verdict === 'pass' && justLen > 0 && justLen < MIN_PASS_NUDGE;

  const handle = async () => {
    if (!verdict || failBlocked) return;
    setSubmitting(true);
    setError('');
    try {
      const r = await classroomAPI.submitVerdict(submissionId, verdict, justification.trim());
      setExisting(r.data);
      onSubmitted?.(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit verdict');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3 border border-[#2F8A92]/20 rounded p-4" data-testid="teacher-verdict">
      <h4 className="font-hud text-sm tracking-widest uppercase text-[#2F8A92] flex items-center gap-2">
        <AlertCircle size={14} /> TEACHER VERDICT
      </h4>

      <div className="flex gap-2">
        <button
          data-testid="verdict-pass-btn"
          onClick={() => setVerdict('pass')}
          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border rounded font-hud text-xs tracking-wider transition-all duration-300 ${
            verdict === 'pass'
              ? 'border-[#2F8A92] bg-[#2F8A92]/15 text-[#2F8A92]'
              : 'border-[#2F8A92]/20 text-white/80 hover:border-[#2F8A92]/40'
          }`}
        >
          <ThumbsUp size={12} /> PASS
        </button>
        <button
          data-testid="verdict-fail-btn"
          onClick={() => setVerdict('fail')}
          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border rounded font-hud text-xs tracking-wider transition-all duration-300 ${
            verdict === 'fail'
              ? 'border-red-400 bg-red-400/15 text-red-400'
              : 'border-[#2F8A92]/20 text-white/80 hover:border-red-400/40'
          }`}
        >
          <ThumbsDown size={12} /> FAIL
        </button>
      </div>

      <div>
        <label className="font-hud text-[10px] tracking-wider text-[#D4A84A] flex items-center justify-between">
          <span>
            JUSTIFICATION
            {verdict === 'fail' && <span className="text-red-400"> · REQUIRED ({MIN_FAIL_JUSTIFICATION}+ chars)</span>}
            {verdict === 'pass' && <span className="text-[#52525B]"> · OPTIONAL</span>}
          </span>
          <span className="font-mono text-[9px] text-[#52525B]">{justLen} chars</span>
        </label>
        <textarea
          data-testid="justification-input"
          className="hud-input text-xs min-h-[100px] resize-y mt-1"
          placeholder={
            verdict === 'fail'
              ? 'Explain what the submission missed — at least 40 characters. This goes to the student and the audit trail.'
              : verdict === 'pass'
              ? 'Optional — name one strength or one area to grow.'
              : 'Pick Pass or Fail first.'
          }
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          disabled={!verdict || submitting}
        />
        {failBlocked && (
          <p className="font-mono text-[10px] text-red-400 mt-1">
            Need {MIN_FAIL_JUSTIFICATION - justLen} more characters before you can submit a Fail verdict.
          </p>
        )}
        {passNudge && (
          <p className="font-mono text-[10px] text-[#D4A84A] mt-1">
            Nudge: if you're writing a justification, make it at least {MIN_PASS_NUDGE} characters — or leave it blank.
          </p>
        )}
      </div>

      {error && <p className="font-mono text-[10px] text-red-400">{error}</p>}

      <button
        data-testid="submit-verdict-btn"
        onClick={handle}
        disabled={!verdict || failBlocked || submitting}
        className="w-full font-hud text-xs tracking-wider text-[#0A0A0A] bg-[#D4A84A] px-3 py-2 hover:bg-[#D4A84A]/80 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
      >
        {submitting ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
        {existing ? 'UPDATE VERDICT' : 'SUBMIT VERDICT'}
      </button>

      <p className="font-mono text-[9px] text-[#52525B]">
        Pass/Fail runs in parallel with the Chain Keeper Rating. Both are required for full review.
        The student sees your verdict and justification. Pattern of unjustified Fails auto-flags for admin review.
      </p>
    </div>
  );
}

/* ─── Teacher: create a classroom ─── */
function CreateClassroomForm({ onCreated, onCancel }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handle = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const r = await classroomAPI.create(name.trim(), desc.trim());
      onCreated(r.data);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border border-[#D4A84A]/20 rounded p-4 space-y-2" data-testid="create-classroom-form">
      <input
        data-testid="classroom-name-input"
        className="hud-input text-sm"
        placeholder="Classroom name (e.g., World History 11)"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <input
        data-testid="classroom-desc-input"
        className="hud-input text-sm"
        placeholder="Description (optional)"
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
      />
      <div className="flex gap-2">
        <button
          data-testid="save-classroom-btn"
          onClick={handle}
          disabled={submitting || !name.trim()}
          className="font-hud text-xs tracking-wider text-[#0A0A0A] bg-[#D4A84A] px-3 py-1.5 hover:bg-[#D4A84A]/80 disabled:opacity-40"
        >
          CREATE
        </button>
        <button
          onClick={onCancel}
          className="font-hud text-xs text-white/80 tracking-wider hover:text-[#2F8A92]"
        >
          CANCEL
        </button>
      </div>
    </div>
  );
}

/* ─── Invite students ─── */
function InviteStudents({ classroomId, onInvited }) {
  const [emails, setEmails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handle = async () => {
    const list = emails.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) return;
    setSubmitting(true);
    try {
      const r = await classroomAPI.invite(classroomId, list);
      setResult(r.data);
      setEmails('');
      onInvited?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid="invite-students">
      <input
        data-testid="invite-emails-input"
        className="hud-input text-xs flex-1"
        placeholder="student@email.com, another@email.com…"
        value={emails}
        onChange={(e) => setEmails(e.target.value)}
      />
      <button
        data-testid="invite-btn"
        onClick={handle}
        disabled={submitting}
        className="font-hud text-[10px] tracking-wider text-[#2F8A92] border border-[#2F8A92]/30 px-3 py-1.5 hover:bg-[#2F8A92]/10 disabled:opacity-40 flex items-center gap-1"
      >
        <UserPlus size={10} /> INVITE
      </button>
      {result && (
        <span className="font-mono text-[10px] text-white/80">
          +{result.added?.length || 0} · {result.roster_count}/{result.seat_limit}
        </span>
      )}
    </div>
  );
}

/* ─── Create assignment ─── */
function CreateAssignment({ classroomId, onCreated }) {
  const [chains, setChains] = useState([]);
  const [chainId, setChainId] = useState('');
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    chainAPI.list().then((r) => setChains(r.data)).catch(() => {});
  }, []);

  const handle = async () => {
    if (!chainId || !title.trim()) return;
    setSubmitting(true);
    try {
      await classroomAPI.createAssignment(classroomId, { chain_id: chainId, title: title.trim(), instructions });
      setTitle('');
      setInstructions('');
      setChainId('');
      onCreated();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border border-[#2F8A92]/10 rounded p-3 space-y-2" data-testid="create-assignment">
      <h5 className="font-hud text-[10px] tracking-widest uppercase text-[#2F8A92]">New Assignment</h5>
      <select
        data-testid="assignment-chain-select"
        value={chainId}
        onChange={(e) => setChainId(e.target.value)}
        className="hud-input text-xs w-full"
      >
        <option value="">— Select chain —</option>
        {chains.map((c) => (
          <option key={c.id} value={c.id}>{c.subject}</option>
        ))}
      </select>
      <input
        data-testid="assignment-title-input"
        className="hud-input text-sm"
        placeholder="Assignment title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <textarea
        data-testid="assignment-instructions-input"
        className="hud-input text-xs min-h-[60px] resize-y"
        placeholder="Instructions (optional)"
        value={instructions}
        onChange={(e) => setInstructions(e.target.value)}
      />
      <button
        data-testid="assign-btn"
        onClick={handle}
        disabled={submitting || !chainId || !title.trim()}
        className="font-hud text-[10px] tracking-wider text-[#0A0A0A] bg-[#D4A84A] px-3 py-1.5 hover:bg-[#D4A84A]/80 disabled:opacity-40 flex items-center gap-1"
      >
        <Plus size={10} /> ASSIGN TO ROSTER
      </button>
    </div>
  );
}

/* ─── Teacher: submission review + rating ─── */
function SubmissionReview({ submission, onClose, onRated }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/80 flex items-center justify-center p-4" data-testid="submission-review">
      <div className="bg-[#0A0A0A] border border-[#2F8A92]/20 rounded max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#0A0A0A] border-b border-[#2F8A92]/10 px-4 py-3 flex items-center justify-between">
          <div>
            <h4 className="font-historical text-[#D4A84A] text-sm">{submission.title}</h4>
            <p className="font-mono text-[10px] text-[#52525B]">
              {submission.student_name} · {new Date(submission.submitted_at).toLocaleString()}
            </p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-[#2F8A92]"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-4">
          {(submission.sections || []).map((s) => (
            <div key={s.id} className="border border-[#2F8A92]/10 rounded p-3">
              <div className="flex items-center gap-2 mb-2">
                <h5 className="font-historical text-sm text-[#D4A84A]">{s.heading || 'Untitled section'}</h5>
                {s.step_key && (
                  <span className="font-hud text-[8px] tracking-wider uppercase px-2 py-0.5 rounded border border-[#2F8A92]/20 text-[#2F8A92]">
                    {s.step_key.replace('_', ' ')}
                  </span>
                )}
              </div>
              <p className="font-mono text-xs text-white/80 leading-relaxed whitespace-pre-wrap">{s.body}</p>
              {(s.citations || []).map((c) => (
                <div key={c.id} className="mt-2 text-[10px] font-mono text-[#2F8A92]">
                  <a href={c.url} target="_blank" rel="noreferrer">↗ {c.title || c.url}</a>
                </div>
              ))}
            </div>
          ))}
          <TeacherVerdict
            submissionId={submission.id}
            onSubmitted={() => onRated?.()}
          />
          <ChainKeeperRating
            submissionId={submission.id}
            existingRating={submission.teacher_rating}
            onRated={(r) => { onRated?.(r); onClose(); }}
          />
        </div>
      </div>
    </div>
  );
}

/* ─── Teacher view ─── */
function TeacherClassroomView({ classroom, onBack, onRefresh }) {
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [reviewing, setReviewing] = useState(null);

  const load = useCallback(() => {
    classroomAPI.listAssignments(classroom.id).then((r) => setAssignments(r.data)).catch(() => {});
    classroomAPI.listSubmissions(classroom.id).then((r) => setSubmissions(r.data)).catch(() => {});
  }, [classroom.id]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="teacher-classroom-view">
      <div className="flex items-center gap-3">
        <button
          data-testid="classroom-back-btn"
          onClick={onBack}
          className="font-hud text-[10px] tracking-wider text-[#52525B] hover:text-[#2F8A92]"
        >
          ← BACK
        </button>
        <div>
          <h3 className="font-historical text-[#D4A84A] text-lg">{classroom.name}</h3>
          <p className="font-mono text-[10px] text-[#52525B]">
            {classroom.student_ids?.length || 0} / {classroom.seat_limit} students
          </p>
        </div>
      </div>

      <InviteStudents classroomId={classroom.id} onInvited={() => { onRefresh?.(); load(); }} />

      <CreateAssignment classroomId={classroom.id} onCreated={load} />

      <div>
        <h5 className="font-hud text-[10px] tracking-widest uppercase text-[#2F8A92] mb-2">Assignments</h5>
        <div className="space-y-2">
          {assignments.map((a) => (
            <div key={a.id} className="border border-[#2F8A92]/10 rounded p-3" data-testid={`assignment-${a.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-historical text-sm text-[#D4A84A]">{a.title}</p>
                  <p className="font-mono text-[10px] text-[#52525B]">{a.chain_subject} · {a.assigned_to?.length || 0} assigned</p>
                </div>
              </div>
            </div>
          ))}
          {assignments.length === 0 && (
            <p className="font-mono text-[10px] text-[#52525B]">No assignments yet.</p>
          )}
        </div>
      </div>

      <div>
        <h5 className="font-hud text-[10px] tracking-widest uppercase text-[#2F8A92] mb-2">
          Submission Inbox ({submissions.length})
        </h5>
        <div className="space-y-2">
          {submissions.map((s) => (
            <button
              key={s.id}
              data-testid={`submission-${s.id}`}
              onClick={() => setReviewing(s)}
              className="w-full text-left border border-[#2F8A92]/10 rounded p-3 hover:border-[#2F8A92]/30 transition-all"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-historical text-sm text-[#D4A84A]">{s.title}</p>
                  <p className="font-mono text-[10px] text-[#52525B]">
                    {s.student_name} · {new Date(s.submitted_at).toLocaleString()}
                  </p>
                </div>
                {s.teacher_rating ? (
                  <span className="font-hud text-xs text-[#D4A84A] flex items-center gap-1">
                    <CheckCircle2 size={12} /> {s.teacher_rating.total_score}/25
                  </span>
                ) : (
                  <span className="font-hud text-[10px] text-[#2F8A92] flex items-center gap-1">
                    <Clock size={10} /> AWAITING REVIEW
                  </span>
                )}
              </div>
            </button>
          ))}
          {submissions.length === 0 && (
            <p className="font-mono text-[10px] text-[#52525B]">No submissions yet.</p>
          )}
        </div>
      </div>

      {reviewing && (
        <SubmissionReview
          submission={reviewing}
          onClose={() => setReviewing(null)}
          onRated={load}
        />
      )}
    </div>
  );
}

/* ─── Student view ─── */
function StudentAssignmentView({ classroom, onBack }) {
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    classroomAPI.myAssignments().then((r) => {
      setAssignments(r.data.filter((a) => a.classroom_id === classroom.id));
    }).catch(() => {});
  }, [classroom.id]);

  return (
    <div className="space-y-3" data-testid="student-classroom-view">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="font-hud text-[10px] tracking-wider text-[#52525B] hover:text-[#2F8A92]">← BACK</button>
        <h3 className="font-historical text-[#D4A84A] text-lg">{classroom.name}</h3>
      </div>
      <p className="font-mono text-[10px] text-[#52525B]">
        Your assignments for this classroom. Draft in the Notepad tab, then submit from there.
      </p>
      <div className="space-y-2">
        {assignments.map((a) => (
          <div key={a.id} className="border border-[#2F8A92]/10 rounded p-3" data-testid={`my-assignment-${a.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-historical text-sm text-[#D4A84A]">{a.title}</p>
                <p className="font-mono text-[10px] text-white/80 mt-1">{a.chain_subject}</p>
                {a.instructions && <p className="font-mono text-[10px] text-[#52525B] mt-1">{a.instructions}</p>}
              </div>
              {a.submitted ? (
                <span className="font-hud text-[10px] text-[#52525B] flex items-center gap-1">
                  <CheckCircle2 size={10} /> SUBMITTED
                </span>
              ) : (
                <span className="font-hud text-[10px] text-[#2F8A92]">PENDING</span>
              )}
            </div>
          </div>
        ))}
        {assignments.length === 0 && (
          <p className="font-mono text-[10px] text-[#52525B]">No assignments in this classroom yet.</p>
        )}
      </div>
    </div>
  );
}

/* ─── Main Classroom (Mode C) ─── */
export default function Classroom() {
  const { user } = useAuth();
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const isTeacher = user?.role === 'admin' || user?.role === 'teacher';

  const load = () => {
    setLoading(true);
    classroomAPI.list()
      .then((r) => setClassrooms(r.data))
      .catch(() => setClassrooms([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="font-hud text-[#2F8A92] text-sm tracking-wider animate-pulse p-4">LOADING CLASSROOMS…</div>;
  }

  if (selected) {
    return selected.role === 'teacher'
      ? <TeacherClassroomView classroom={selected} onBack={() => { setSelected(null); load(); }} onRefresh={load} />
      : <StudentAssignmentView classroom={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <div className="space-y-4" data-testid="classroom-mode-c">
      <div className="flex items-center justify-between">
        <h3 className="font-hud text-[#2F8A92] text-sm tracking-widest uppercase flex items-center gap-2">
          <Users size={14} /> CLASSROOMS
        </h3>
        {isTeacher && !creating && (
          <button
            data-testid="new-classroom-btn"
            onClick={() => setCreating(true)}
            className="font-hud text-xs tracking-wider text-[#0A0A0A] bg-[#D4A84A] px-3 py-1.5 hover:bg-[#D4A84A]/80 flex items-center gap-1"
          >
            <Plus size={10} /> NEW CLASSROOM
          </button>
        )}
      </div>

      {creating && (
        <CreateClassroomForm
          onCreated={() => { setCreating(false); load(); }}
          onCancel={() => setCreating(false)}
        />
      )}

      {classrooms.length === 0 && !creating ? (
        <p className="font-mono text-[11px] text-[#52525B]">
          {isTeacher ? 'No classrooms yet. Create one to get started.' : 'You have not been added to a classroom yet. Ask your teacher for an invite.'}
        </p>
      ) : (
        <div className="space-y-2">
          {classrooms.map((c) => (
            <button
              key={c.id}
              data-testid={`classroom-${c.id}`}
              onClick={() => setSelected(c)}
              className="w-full text-left border border-[#2F8A92]/10 rounded p-3 hover:border-[#2F8A92]/30 transition-all"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-historical text-sm text-[#D4A84A]">{c.name}</p>
                  <p className="font-mono text-[10px] text-white/80 mt-0.5">{c.description}</p>
                  <p className="font-mono text-[9px] text-[#52525B] mt-1">
                    {c.role === 'teacher'
                      ? `Teacher · ${c.student_ids?.length || 0}/${c.seat_limit} students`
                      : `Student · teacher: ${c.teacher_name}`}
                  </p>
                </div>
                <span className="font-hud text-[9px] tracking-wider uppercase px-2 py-0.5 rounded border"
                  style={{
                    borderColor: c.role === 'teacher' ? '#D4A84A33' : '#2F8A9233',
                    color: c.role === 'teacher' ? '#D4A84A' : '#2F8A92',
                  }}
                >
                  {c.role}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
